import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  AccountBalanceWallet, ArrowDownward, ArrowUpward, Groups, ShoppingCart, Speed, TrendingUp,
} from '@mui/icons-material';
import { PulseMetric } from './missionData';
import { s, cardSx, trendTone, reduceMotion } from './missionTokens';

const ICONS = {
  revenue: TrendingUp,
  cash: AccountBalanceWallet,
  orders: ShoppingCart,
  efficiency: Speed,
  people: Groups,
} as const;

const TINT = {
  revenue: [s.success, s.successSoft],
  cash: [s.primary, s.primarySoft],
  orders: [s.violet, s.violetSoft],
  efficiency: [s.info, s.infoSoft],
  people: [s.warning, s.warningSoft],
} as const;

/**
 * Business Pulse — the organisation's current state, not five disconnected
 * KPI tiles.
 *
 * The cards share one row and one baseline so they read as a single instrument
 * panel, and each carries its own direction-of-good: a rise in receivables or
 * attrition is rendered red, because "up" is not universally good and colouring
 * it green would actively mislead.
 */
const BusinessPulse: React.FC<{ metrics: PulseMetric[] }> = ({ metrics }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: `repeat(${metrics.length}, 1fr)` },
      gap: 1.5,
    }}
  >
    {metrics.map((m, i) => {
      const Icon = ICONS[m.icon];
      const [fg, bg] = TINT[m.icon];
      const { up, color } = trendTone(m.delta, m.higherIsBetter ?? true);
      const Arrow = up ? ArrowUpward : ArrowDownward;

      return (
        <Box
          key={m.key}
          sx={{
            ...cardSx,
            p: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            animation: `pulseIn .5s cubic-bezier(.2,.7,.2,1) ${i * 60}ms both`,
            '@keyframes pulseIn': {
              from: { opacity: 0, transform: 'translateY(8px)' },
              to: { opacity: 1, transform: 'none' },
            },
            transition: 'box-shadow .25s ease, transform .25s ease',
            '&:hover': { boxShadow: s.shadowMd, transform: 'translateY(-2px)' },
            ...reduceMotion,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: bg,
            }}
          >
            <Icon sx={{ fontSize: 19, color: fg }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontSize: '1.22rem', fontWeight: 800, color: s.heading, lineHeight: 1.15, letterSpacing: '-0.02em' }}
            >
              {m.value}
            </Typography>
            <Typography sx={{ fontSize: '0.74rem', color: s.muted, fontWeight: 500, lineHeight: 1.35 }} noWrap>
              {m.label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
              <Arrow sx={{ fontSize: 12, color }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color }}>
                {Math.abs(m.delta)}%
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default BusinessPulse;
